import React, { useState } from 'react';
import { MOCK_BOOTHS } from '../constants';
import { Booth } from '../types';
import { getBoothStrategy } from '../services/geminiService';
import { MapPin, Users, Sparkles, FileText, Activity, UserCheck, AlertCircle, Map, List, ChevronRight } from 'lucide-react';

const BoothManagement: React.FC = () => {
  const [selectedBooth, setSelectedBooth] = useState<Booth | null>(null);
  const [strategy, setStrategy] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'map' | 'details'>('map');
  const [mapPopupBooth, setMapPopupBooth] = useState<Booth | null>(null);

  const handleAnalyze = async (booth: Booth) => {
    setSelectedBooth(booth);
    setViewMode('details');
    setLoading(true);
    setStrategy("");
    const result = await getBoothStrategy(booth);
    setStrategy(result);
    setLoading(false);
  };

  const handleMapMarkerClick = (booth: Booth) => {
    setMapPopupBooth(booth);
  };

  // Helper to normalize lat/long to % for the map container
  const getMapCoordinates = (lat: number, lon: number) => {
    const lats = MOCK_BOOTHS.map(b => b.latitude);
    const lons = MOCK_BOOTHS.map(b => b.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    // Add padding (10%)
    const latRange = maxLat - minLat || 1;
    const lonRange = maxLon - minLon || 1;

    // Invert Lat because screen Y coordinates go down, but latitude goes up
    const y = 90 - ((lat - minLat) / latRange) * 80; 
    const x = 10 + ((lon - minLon) / lonRange) * 80;

    return { x: `${x}%`, y: `${y}%` };
  };

  const renderMap = () => (
    <div className="relative w-full h-full bg-slate-100 rounded-xl overflow-hidden border border-slate-200 shadow-inner group">
      {/* Map Grid Background */}
      <div className="absolute inset-0" style={{ 
          backgroundImage: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)', 
          backgroundSize: '40px 40px' 
        }}>
      </div>
      
      {/* Map Content */}
      <div className="absolute inset-0 p-8">
         <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-sm border border-slate-200 text-xs text-slate-500 z-10">
            <h4 className="font-bold mb-1">Booth Trend Legend</h4>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500"></span> BJP Lead</div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-600"></span> AIUDF Lead</div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span> INC Lead</div>
         </div>

         {MOCK_BOOTHS.map(booth => {
            const coords = getMapCoordinates(booth.latitude, booth.longitude);
            const colorClass = booth.lastElectionTrend === 'BJP' ? 'bg-orange-500 border-orange-200' : 
                               booth.lastElectionTrend === 'AIUDF' ? 'bg-green-600 border-green-200' : 'bg-blue-500 border-blue-200';
            
            return (
               <div 
                  key={booth.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all hover:scale-110 z-0 hover:z-20"
                  style={{ left: coords.x, top: coords.y }}
                  onClick={() => handleMapMarkerClick(booth)}
               >
                  <div className={`w-4 h-4 rounded-full border-2 ${colorClass} shadow-lg relative`}>
                     <div className={`absolute -inset-2 rounded-full opacity-20 ${colorClass.split(' ')[0]} animate-ping`}></div>
                  </div>
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                     Booth {booth.number}
                  </div>
               </div>
            );
         })}
      </div>

      {/* Selected Marker Popup */}
      {mapPopupBooth && (
        <div className="absolute bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-80 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-30 animate-fade-in-up">
           <div className="flex justify-between items-start mb-2">
              <h3 className="font-bold text-slate-800">Booth {mapPopupBooth.number}</h3>
              <button onClick={() => setMapPopupBooth(null)} className="text-slate-400 hover:text-slate-600">&times;</button>
           </div>
           <p className="text-sm text-slate-600 mb-3">{mapPopupBooth.name}</p>
           
           <div className="grid grid-cols-2 gap-2 text-xs mb-4">
              <div className="bg-slate-50 p-2 rounded border border-slate-100">
                 <span className="text-slate-400 block">Voters</span>
                 <span className="font-bold text-slate-700">{mapPopupBooth.totalVoters}</span>
              </div>
              <div className="bg-slate-50 p-2 rounded border border-slate-100">
                 <span className="text-slate-400 block">P.P. Strength</span>
                 <span className={`font-bold ${mapPopupBooth.verifiedPannaPramukhs < mapPopupBooth.targetPannaPramukhs ? 'text-red-500' : 'text-green-600'}`}>
                    {mapPopupBooth.verifiedPannaPramukhs}/{mapPopupBooth.targetPannaPramukhs}
                 </span>
              </div>
           </div>

           <button 
             onClick={() => handleAnalyze(mapPopupBooth)}
             className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-lg text-sm font-medium transition-colors"
           >
             View Full Strategy <ChevronRight className="w-4 h-4" />
           </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-slate-50">
      
      {/* List Sidebar */}
      <div className="w-1/3 min-w-[300px] border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-slate-700">Shakti Kendra List</h3>
            <p className="text-xs text-slate-500">Constituency Breakdown</p>
          </div>
          <button 
            onClick={() => setViewMode('map')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'map' ? 'bg-bjp-saffron text-white shadow-sm' : 'text-slate-400 hover:bg-slate-100'}`}
            title="View Map"
          >
            <Map className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {MOCK_BOOTHS.map((booth) => (
            <div 
              key={booth.id}
              onClick={() => handleAnalyze(booth)}
              className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-orange-50 transition-colors ${selectedBooth?.id === booth.id && viewMode === 'details' ? 'bg-orange-50 border-l-4 border-l-bjp-saffron' : 'border-l-4 border-l-transparent'}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-slate-800 text-sm">Booth {booth.number}</h4>
                  <p className="text-xs text-slate-500 truncate w-48">{booth.name}</p>
                </div>
                <div className={`text-xs px-2 py-0.5 rounded ${booth.lastElectionTrend === 'BJP' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                  {booth.lastElectionTrend}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <Users className="w-3 h-3" />
                <span>Panna Pramukhs: <span className={booth.verifiedPannaPramukhs < booth.targetPannaPramukhs ? 'text-red-500 font-bold' : 'text-green-600 font-bold'}>{booth.verifiedPannaPramukhs}/{booth.targetPannaPramukhs}</span></span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Detail Area */}
      <div className="flex-1 overflow-y-auto p-6 relative">
        
        {/* Toggle Header in Main Area */}
        <div className="flex items-center justify-between mb-4">
           <h2 className="text-xl font-bold text-slate-800">
              {viewMode === 'map' ? 'Tactical Map View' : 'Booth Strategy Detail'}
           </h2>
           <div className="flex bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
              <button 
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'map' ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <Map className="w-3 h-3" /> Map View
              </button>
              <button 
                onClick={() => selectedBooth ? setViewMode('details') : alert('Please select a booth first.')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'details' ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <List className="w-3 h-3" /> Detail View
              </button>
           </div>
        </div>

        {viewMode === 'map' ? renderMap() : (
          selectedBooth ? (
          <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
            
            {/* Header Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <MapPin className="text-bjp-saffron" />
                    {selectedBooth.name}
                  </h2>
                  <p className="text-slate-500 mt-1">Mandal: {selectedBooth.mandal} | Shakti Kendra: {selectedBooth.shaktiKendra}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-600 bg-slate-50 px-3 py-1 rounded-md inline-flex border border-slate-100">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span>Lat: {selectedBooth.latitude}, Long: {selectedBooth.longitude}</span>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Key Metrics Section */}
              <div className="mt-6 bg-slate-50 rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                    <Activity className="w-4 h-4 text-slate-500" />
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Booth Vital Stats</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Voters */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                     <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-semibold text-slate-500 uppercase">Total Voters</span>
                     </div>
                     <div className="text-2xl font-bold text-slate-800">{selectedBooth.totalVoters.toLocaleString()}</div>
                     <div className="text-xs text-slate-400 mt-1">Registered Electorate</div>
                  </div>
                  
                  {/* Panna Pramukhs */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-bjp-saffron hover:shadow-md transition-shadow">
                     <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-4 h-4 text-bjp-saffron" />
                        <span className="text-xs font-semibold text-slate-500 uppercase">Verified P.P.</span>
                     </div>
                     <div className="text-2xl font-bold text-slate-800">
                        {selectedBooth.verifiedPannaPramukhs} 
                        <span className="text-sm font-normal text-slate-400 ml-1">/ {selectedBooth.targetPannaPramukhs}</span>
                     </div>
                     <div className="w-full bg-slate-100 h-1.5 mt-2 rounded-full overflow-hidden">
                        <div 
                            className={`h-full rounded-full ${selectedBooth.verifiedPannaPramukhs >= selectedBooth.targetPannaPramukhs ? 'bg-bjp-green' : 'bg-bjp-saffron'}`}
                            style={{ width: `${Math.min((selectedBooth.verifiedPannaPramukhs / selectedBooth.targetPannaPramukhs) * 100, 100)}%` }}
                        ></div>
                     </div>
                  </div>

                  {/* Primary Issue */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-500 hover:shadow-md transition-shadow">
                     <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-semibold text-slate-500 uppercase">Top Issue</span>
                     </div>
                     <div className="text-lg font-bold text-slate-800 truncate" title={selectedBooth.keyIssues[0]}>
                        {selectedBooth.keyIssues[0]}
                     </div>
                     <div className="text-xs text-slate-400 mt-1">Priority Concern</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Demographics */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-4">Demographic Breakdown</h3>
              <div className="space-y-3">
                {selectedBooth.demographics.map((demo, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{demo.community}</span>
                      <span className="font-medium text-slate-900">{demo.percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full">
                      <div 
                        className="h-2 rounded-full bg-slate-600" 
                        style={{ width: `${demo.percentage}%`, backgroundColor: idx === 0 ? '#FF9933' : '#138808' }} // Simple color logic
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Strategy Section */}
            <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-100">
              <h3 className="flex items-center gap-2 font-bold text-slate-800 text-lg mb-4">
                <Sparkles className="w-5 h-5 text-bjp-saffron" />
                Sangathan AI Advice
              </h3>
              
              {loading ? (
                <div className="flex items-center gap-2 text-slate-500 animate-pulse">
                  <div className="w-4 h-4 bg-bjp-saffron rounded-full animate-bounce"></div>
                  Generating booth-level strategy...
                </div>
              ) : (
                <div className="prose prose-sm prose-orange text-slate-700 whitespace-pre-line">
                  {strategy}
                </div>
              )}
              
              {!loading && strategy && (
                <div className="mt-4 pt-4 border-t border-orange-100 flex gap-2">
                   <button className="flex items-center gap-1 text-xs font-medium bg-white border border-slate-200 px-3 py-1.5 rounded shadow-sm hover:bg-slate-50">
                      <FileText className="w-3 h-3" /> Save to Report
                   </button>
                   <button className="flex items-center gap-1 text-xs font-medium bg-bjp-green text-white px-3 py-1.5 rounded shadow-sm hover:opacity-90">
                      Assign to Gatnayak
                   </button>
                </div>
              )}
            </div>

          </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <MapPin className="w-12 h-12 mb-2 opacity-50" />
              <p>Select a Booth from the sidebar to view details</p>
              <button onClick={() => setViewMode('map')} className="mt-4 text-bjp-saffron font-medium hover:underline text-sm">
                Return to Map
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default BoothManagement;